import React, { useContext, useState } from 'react'
import { getMarket, CircleSpinner } from 'shared/index'
import { useTranslation } from 'react-i18next'
import SspContext from 'selfServicePortal/contexts/SspContext'
import {
	initiateUserUpdate,
	ContactInfoChangeType,
} from 'selfServicePortal/services/CustomerServices/updateUserService'
import { useMutation } from '@tanstack/react-query'
import { getErrorMessage } from 'selfServicePortal/utils'
import { Heading } from 'selfServicePortal/components'
import { ReactComponent as SmartPhone } from 'img/icons/smartphone.svg'
import { ReactComponent as Mail } from 'img/icons/mail.svg'
import { ICustomer } from 'selfServicePortal/interfaces/i-api-customer'
import {
	maskedEmail,
	maskedPhoneNr,
} from '../../SDD/SDDMultistep/MultistepBlocks/SDDAuthenticationBlocks/AuthValidation'
import { OtpDistributionChannel } from 'selfServicePortal/interfaces/i-api-sdd'

interface AddressSecurityCodeProps {
	customer: ICustomer
	onSuccess: (sessionId: string) => void
}

export function AddressSecurityCode({
	customer,
	onSuccess,
}: AddressSecurityCodeProps) {
	const { t } = useTranslation()
	const { customerData } = useContext(SspContext)
	const [radio, setRadio] = useState<'sms' | 'email'>()

	const initiateUserUpdateMutation = useMutation({
		mutationFn: () =>
			initiateUserUpdate({
				customerId: customerData?.customerId,
				firstName: customerData?.firstName,
				lastName: customerData?.lastName,
				phoneNumber: customerData?.mobilePhoneNumber,
				emailAddress: customerData?.email,
				market: getMarket(),
				address: {
					street: customer.street,
					houseNumber: customer.houseNumber,
					city: customer.city,
					postalCode: customer.postalCode,
					apartmentNumber: customer.apartmentNumber,
					shortAddress: `${
						customer.street + ' ' + customer.houseNumber
					}`,
				},
				changeType: ContactInfoChangeType.Address,
				distributionChannel:
					radio === 'sms'
						? OtpDistributionChannel.SMS
						: OtpDistributionChannel.Email,
			}),
		onSuccess: ({ sessionId }) => onSuccess(sessionId),
	})

	if (initiateUserUpdateMutation.isLoading) {
		return <CircleSpinner />
	}

	return (
		<>
			<Heading
				title={t('AddressChangeSecurityTitle')}
				description={t('AddressChangeSecurityDescription')}
			/>

			<div className="radio-group">
				<label className="radio">
					<div className="radio-left">
						<Mail width={20} />
						<span>
							<span>{t('AddressChangeSecurityEmailTo')}</span>
							<span>
								{' ' + maskedEmail(customerData?.email)}
							</span>
						</span>
					</div>
					<input
						type="radio"
						checked={radio === 'email'}
						onChange={() => setRadio('email')}
					/>
				</label>
				<label className="radio">
					<div className="radio-left">
						<SmartPhone width={20} />
						<span>
							<span>{t('AddressChangeSecuritySmsTo')}</span>
							<span>
								{' ' +
									maskedPhoneNr(
										customerData?.mobilePhoneNumber,
									)}
							</span>
						</span>
					</div>
					<input
						type="radio"
						checked={radio === 'sms'}
						onChange={() => setRadio('sms')}
					/>
				</label>
			</div>
			{initiateUserUpdateMutation.isError && (
				<div className="error">
					{getErrorMessage(initiateUserUpdateMutation.error)}
				</div>
			)}
			<div>
				<button
					type="button"
					className="btn btn-primary"
					disabled={radio === undefined}
					onClick={() => initiateUserUpdateMutation.mutate()}>
					{t('AddressChangeButton')}
				</button>
			</div>
		</>
	)
}
